/**
 * Swoopy Season theme — cart drawer, navigation, carousels, animations
 * Uses Shopify Section Rendering API for atomic cart updates.
 */
(function () {
  'use strict';

  const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const t = (key, fallback) => (window.SwoopyTheme.strings && window.SwoopyTheme.strings[key]) || fallback || '';

  const getSectionIds = () => window.SwoopyTheme?.sectionIds || {};

  const getSectionsToRefresh = () => {
    const ids = getSectionIds();
    const list = [ids.cartDrawer, ids.header].filter(Boolean);
    return list.length ? list.join(',') : 'cart-drawer,header';
  };

  const isCartDrawerSection = (sectionId) => {
    const cartDrawerId = getSectionIds().cartDrawer;
    return sectionId === 'cart-drawer' || sectionId === cartDrawerId || sectionId.includes('cart-drawer');
  };

  const isHeaderSection = (sectionId) => {
    const headerId = getSectionIds().header;
    return sectionId === 'header' || sectionId === headerId || sectionId.includes('__header');
  };

  /* ── Cart ─────────────────────────────────────────────── */

  const Cart = {
    drawer: null,
    backdrop: null,
    lastFocus: null,
    bound: false,

    init() {
      this.drawer = document.getElementById('cart-drawer');
      this.backdrop = document.getElementById('cart-backdrop');

      if (!this.bound) {
        this.bound = true;

        document.addEventListener('click', (e) => {
          const open = e.target.closest('[data-cart-open]');
          if (open) {
            e.preventDefault();
            this.lastFocus = open;
            this.open();
            return;
          }
          const close = e.target.closest('[data-cart-close]');
          if (close) {
            e.preventDefault();
            this.close();
            return;
          }
          const add = e.target.closest('[data-add-to-cart]');
          if (add) {
            e.preventDefault();
            this.addItem(add);
            return;
          }
          const qty = e.target.closest('[data-cart-qty-change]');
          if (qty) {
            e.preventDefault();
            this.changeQuantity(qty.dataset.key, parseInt(qty.dataset.qty, 10));
            return;
          }
          const remove = e.target.closest('[data-cart-remove]');
          if (remove) {
            e.preventDefault();
            this.changeQuantity(remove.dataset.key, 0);
            return;
          }
          const clear = e.target.closest('[data-cart-clear]');
          if (clear) {
            e.preventDefault();
            this.clear();
          }
        });

        document.addEventListener('submit', (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          const addBtn = form.querySelector('[data-add-to-cart]');
          if (!addBtn) return;
          e.preventDefault();
          this.addItem(addBtn);
        });

        document.addEventListener('keydown', (e) => {
          if (!this.isOpen()) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
          }
          if (e.key === 'Tab') {
            this.trapFocus(e);
          }
        });
      }

      this.backdrop?.addEventListener('click', () => this.close());
      this.refreshCount();
    },

    isOpen() {
      return this.drawer?.classList.contains('is-open');
    },

    open() {
      this.drawer?.classList.add('is-open');
      this.drawer?.setAttribute('aria-hidden', 'false');
      this.backdrop?.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const closeBtn = this.drawer?.querySelector('[data-cart-close]');
        closeBtn?.focus();
      });
    },

    close() {
      this.drawer?.classList.remove('is-open');
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.backdrop?.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      this.lastFocus?.focus?.();
    },

    trapFocus(e) {
      const focusables = this.drawer?.querySelectorAll(FOCUSABLE);
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    async addItem(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalLabel = button.textContent.trim();
      const wasDisabled = button.disabled;
      button.disabled = true;
      button.dataset.loading = 'true';
      button.textContent = t('addingToCart', 'Adding…');

      try {
        const res = await fetch(`${window.SwoopyTheme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }],
            sections: getSectionsToRefresh(),
            sections_url: window.location.pathname || '/',
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }
        await this.syncCartUI(data.sections);
        this.resetButton(button, originalLabel, wasDisabled);
        if (window.SwoopyTheme.cartType === 'page') {
          window.location.href = window.SwoopyTheme.routes.cartUrl;
        } else {
          this.open();
        }
      } catch (err) {
        console.error(err);
        this.resetButton(button, originalLabel, wasDisabled);
        this.showError(err.message || t('cartError', 'Something went wrong.'));
      }
    },

    resetButton(button, label, wasDisabled) {
      button.textContent = label;
      button.disabled = wasDisabled;
      delete button.dataset.loading;
    },

    async changeQuantity(key, quantity) {
      try {
        const res = await fetch(`${window.SwoopyTheme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: getSectionsToRefresh(),
            sections_url: window.location.pathname || '/',
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || 'Cart change failed');
        }
        await this.syncCartUI(data.sections);
      } catch (err) {
        console.error(err);
        this.showError(err.message);
      }
    },

    async clear() {
      try {
        const clearUrl = `${window.SwoopyTheme.routes.cartClear || '/cart/clear'}.js`;
        const res = await fetch(clearUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            sections: getSectionsToRefresh(),
            sections_url: window.location.pathname || '/',
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || 'Clear cart failed');
        }
        await this.syncCartUI(data.sections);
      } catch (err) {
        console.error(err);
        this.showError(err.message);
      }
    },

    async syncCartUI(sections) {
      const updated = this.applySections(sections);
      if (!updated) {
        await this.fetchDrawerSection();
      }
      await this.refreshCount();
    },

    async fetchDrawerSection() {
      const sectionId = getSectionIds().cartDrawer;
      if (!sectionId || !this.drawer) return;

      try {
        const root = window.SwoopyTheme.routes.root || '/';
        const res = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`);
        const html = await res.text();
        if (!res.ok || !html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.getElementById('cart-drawer');
        if (newDrawer) {
          const wasOpen = this.drawer.classList.contains('is-open');
          this.drawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen) {
            this.drawer.classList.add('is-open');
            this.drawer.setAttribute('aria-hidden', 'false');
          }
        }
      } catch (err) {
        console.error(err);
      }
    },

    applySections(sections) {
      if (!sections) return false;

      let updatedDrawer = false;
      let updatedHeader = false;

      Object.entries(sections).forEach(([sectionId, html]) => {
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        if (isCartDrawerSection(sectionId)) {
          const newDrawer = doc.getElementById('cart-drawer');
          const currentDrawer = document.getElementById('cart-drawer');
          if (newDrawer && currentDrawer) {
            const wasOpen = currentDrawer.classList.contains('is-open');
            currentDrawer.innerHTML = newDrawer.innerHTML;
            if (wasOpen) {
              currentDrawer.classList.add('is-open');
              currentDrawer.setAttribute('aria-hidden', 'false');
            }
            updatedDrawer = true;
          }
        } else if (isHeaderSection(sectionId)) {
          const newCount = doc.querySelector('[data-cart-count]');
          if (newCount) {
            document.querySelectorAll('[data-cart-count]').forEach((el) => {
              el.textContent = newCount.textContent;
              el.classList.toggle('opacity-70', parseInt(newCount.textContent, 10) === 0);
            });
            updatedHeader = true;
          }
        }
      });

      return updatedDrawer || updatedHeader;
    },

    async refreshCount() {
      try {
        const res = await fetch(`${window.SwoopyTheme.routes.cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await res.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('opacity-70', cart.item_count === 0);
        });
        document.querySelectorAll('[data-cart-open]').forEach((el) => {
          el.setAttribute('aria-label', `Open cart with ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
        });
        this.updateBuyButtons(cart);
      } catch (err) {
        console.error(err);
      }
    },

    updateBuyButtons(cart) {
      document.querySelectorAll('[data-variant-id]').forEach((root) => {
        const variantId = root.dataset.variantId;
        if (!variantId) return;
        const item = cart.items.find((i) => String(i.variant_id) === String(variantId));
        const stickyBtn = root.matches('[data-sticky-btn]') ? root : root.querySelector?.('[data-sticky-btn]');
        const packBtn = root.matches('[data-pack-btn]') ? root : root.querySelector?.('[data-pack-btn]');
        const qtyEl = root.querySelector?.('[data-sticky-qty]');

        if (qtyEl) {
          qtyEl.textContent = item
            ? `${item.quantity} ${t('inCart', 'in cart')}`
            : t('tapBelow', 'One complete game in the box');
        }

        [stickyBtn, packBtn].forEach((btn) => {
          if (!btn || btn.disabled) return;
          if (item && packBtn === btn) {
            btn.textContent = `${t('addAnother', 'Add another')} pack (${item.quantity} ${t('inCart', 'in cart')})`;
          } else if (item) {
            btn.textContent = t('addAnother', 'Add another');
          } else {
            btn.textContent = t('addToCart', 'Add to cart');
          }
        });
      });
    },

    showError(message) {
      let el = document.getElementById('cart-error-toast');
      if (!el) {
        el = document.createElement('div');
        el.id = 'cart-error-toast';
        el.className = 'cart-toast';
        el.setAttribute('role', 'alert');
        document.body.appendChild(el);
      }
      el.textContent = message;
      el.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => el.classList.remove('is-visible'), 4000);
    },
  };

  /* ── Mobile menu ──────────────────────────────────────── */

  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.getElementById('mobile-nav');
    if (!toggle || !nav) return;

    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      const openIcon = toggle.querySelector('[data-mobile-menu-icon="open"]');
      const closeIcon = toggle.querySelector('[data-mobile-menu-icon="close"]');
      if (openIcon && closeIcon) {
        openIcon.hidden = open;
        closeIcon.hidden = !open;
      }
      document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => setOpen(!nav.classList.contains('is-open')));

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        setOpen(false);
        toggle.focus();
      }
    });
  }

  /* ── FAQ accordion ────────────────────────────────────── */

  function initFaq() {
    const toggles = document.querySelectorAll('[data-faq-toggle]');
    if (toggles.length === 0) return;

    toggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const chevron = btn.querySelector('.faq-chevron');
        const isOpen = btn.getAttribute('aria-expanded') === 'true';

        toggles.forEach((other) => {
          if (other !== btn) {
            other.setAttribute('aria-expanded', 'false');
            const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            otherPanel?.classList.remove('is-open');
            other.querySelector('.faq-chevron')?.classList.remove('is-open');
          }
        });

        btn.setAttribute('aria-expanded', String(!isOpen));
        panel?.classList.toggle('is-open', !isOpen);
        chevron?.classList.toggle('is-open', !isOpen);
      });
    });

    const first = toggles[0];
    const firstPanel = document.getElementById(first.getAttribute('aria-controls'));
    first.setAttribute('aria-expanded', 'true');
    firstPanel?.classList.add('is-open');
    first.querySelector('.faq-chevron')?.classList.add('is-open');
  }

  /* ── Carousels ────────────────────────────────────────── */

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((rail) => {
      const id = rail.dataset.carousel;
      document.querySelectorAll(`[data-carousel-btn="${id}"]`).forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = parseInt(btn.dataset.direction, 10);
          const card = rail.querySelector('[data-carousel-item]');
          const gap = 16;
          const step = (card?.getBoundingClientRect().width ?? 280) + gap;
          const scrollAmount = id === 'merch'
            ? dir * Math.min(rail.clientWidth * 0.9, 360)
            : dir * step;
          rail.scrollBy({
            left: scrollAmount,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        });
      });

      rail.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          rail.scrollBy({ left: -300, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          rail.scrollBy({ left: 300, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      });
    });
  }

  /* ── Scroll reveal ────────────────────────────────────── */

  function initReveal() {
    const reveals = document.querySelectorAll('.reveal-section');
    if (reveals.length === 0) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = parseFloat(entry.target.dataset.revealDelay || 0);
            setTimeout(() => entry.target.classList.add('is-visible'), delay * 1000);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    reveals.forEach((el) => observer.observe(el));
  }

  /* ── Sticky buy bar ───────────────────────────────────── */

  function initStickyBar() {
    const bar = document.getElementById('sticky-buy-bar');
    if (!bar) return;

    let ticking = false;
    const update = () => {
      bar.classList.toggle('is-visible', window.scrollY > 420);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Hero parallax ────────────────────────────────────── */

  function initHeroParallax() {
    if (prefersReducedMotion()) return;
    const section = document.getElementById('hero-section');
    const artwork = section?.querySelector('.hero-artwork');
    if (!section || !artwork) return;

    let ticking = false;
    const update = () => {
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(rect.height, 1)));
      artwork.style.transform = `translateY(${progress * -32}px) rotate(${progress * 1.25}deg) scale(${1 + progress * 0.03})`;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ── Pack card tilt ───────────────────────────────────── */

  function initPackTilt() {
    if (prefersReducedMotion() || matchMedia('(hover: none)').matches) return;
    document.querySelectorAll('.pack-card').forEach((card) => {
      const glow = card.querySelector('.pack-card-glow');
      const reset = () => {
        card.classList.remove('is-tilting');
        card.style.transform = '';
      };
      card.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const bounds = card.getBoundingClientRect();
        const px = (e.clientX - bounds.left) / bounds.width;
        const py = (e.clientY - bounds.top) / bounds.height;
        const rotateX = (0.5 - py) * 5;
        const rotateY = (px - 0.5) * 7;
        card.classList.add('is-tilting');
        card.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px)`;
        if (glow) {
          glow.style.background = `radial-gradient(circle at ${px * 100}% ${py * 100}%, rgba(255,255,255,0.24), transparent 42%)`;
        }
      });
      card.addEventListener('pointerleave', reset);
      card.addEventListener('pointercancel', reset);
    });
  }

  /* ── Magpie fact flip cards ───────────────────────────── */

  function initFactCards() {
    document.querySelectorAll('[data-fact-card]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const flipped = btn.classList.toggle('is-flipped');
        btn.setAttribute('aria-pressed', String(flipped));
      });
    });
  }

  /* ── Boot ─────────────────────────────────────────────── */

  function boot() {
    Cart.init();
    initMobileMenu();
    initFaq();
    initCarousels();
    initReveal();
    initStickyBar();
    initHeroParallax();
    initPackTilt();
    initFactCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ── Re-initialize on theme editor section reload ───── */

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
